import { create } from 'zustand'

/**
 * Theme selection: follow the OS, or override it explicitly and keep that choice.
 *
 * The value is stored as a bare string rather than through zustand's persist
 * middleware so the pre-paint script in index.html can read it with three lines
 * and no JSON envelope. Both sides must agree on this key and these values.
 */
export const THEME_STORAGE_KEY = 'fleetcore.theme'

export const THEME_MODES = ['system', 'light', 'dark'] as const
export type ThemeMode = (typeof THEME_MODES)[number]
export type ResolvedTheme = 'light' | 'dark'

const DARK_QUERY = '(prefers-color-scheme: dark)'

function isThemeMode(value: string | null): value is ThemeMode {
  return value === 'system' || value === 'light' || value === 'dark'
}

function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY)
    return isThemeMode(stored) ? stored : 'system'
  } catch {
    return 'system'
  }
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_QUERY).matches
    : false
}

export function resolveTheme(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light'
  return mode
}

function applyTheme(resolved: ResolvedTheme): void {
  if (typeof document === 'undefined') return
  document.documentElement.classList.toggle('dark', resolved === 'dark')
  // Tells the browser which palette to use for scrollbars, form controls and the
  // canvas behind the page — otherwise dark mode has white scroll gutters.
  document.documentElement.style.colorScheme = resolved
}

interface ThemeState {
  mode: ThemeMode
  resolved: ResolvedTheme
  setMode: (mode: ThemeMode) => void
  /** Cycles system → light → dark → system, for the single-button toggle. */
  cycleMode: () => void
}

const initialMode = readStoredMode()

export const useThemeStore = create<ThemeState>((set, get) => ({
  mode: initialMode,
  resolved: resolveTheme(initialMode),

  setMode: (mode) => {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, mode)
    } catch {
      /* Storage denied: the override lasts for this tab only. */
    }
    const resolved = resolveTheme(mode)
    applyTheme(resolved)
    set({ mode, resolved })
  },

  cycleMode: () => {
    const order: ThemeMode[] = ['system', 'light', 'dark']
    const next = order[(order.indexOf(get().mode) + 1) % order.length] ?? 'system'
    get().setMode(next)
  },
}))

/**
 * Keeps `system` honest while the app is open — a laptop switching to dark at
 * sunset has to repaint the console without a reload.
 */
export function initTheme(): () => void {
  applyTheme(useThemeStore.getState().resolved)

  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {}

  const query = window.matchMedia(DARK_QUERY)
  const onChange = () => {
    const { mode } = useThemeStore.getState()
    if (mode !== 'system') return
    const resolved: ResolvedTheme = query.matches ? 'dark' : 'light'
    applyTheme(resolved)
    useThemeStore.setState({ resolved })
  }

  query.addEventListener('change', onChange)
  return () => query.removeEventListener('change', onChange)
}
